import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  User, Calendar, Phone, MapPin, Heart, CheckCircle, ShieldCheck,
  ArrowRight, UserCheck, Sun, Armchair, HandHeart, Filter, Loader2,
  ChevronRight, ChevronLeft, Home, Clock, Star
} from 'lucide-react';
import Navbar from '../../components/layout/Navbar';
import Footer from '../../components/layout/Footer';
import apiClient from '../../api/api';
import { useAuth } from '../../context/AuthContext';
import elderlyCareBg from '../../assets/images/ElderlyCare.webp';
import DateRangePicker from '../../components/common/DateRangePicker';
import { todayISO } from '../../components/common/DateInput';
import PhoneInput from '../../components/common/PhoneInput';
import { toCaregiverCard } from './components/caregiverCard';

const STAFF_PAGE_SIZE = 9;

// Selectable options for the Service Details step
const SERVICE_TYPES = [
  {
    value: 'CARETAKER',
    label: 'Caretaker',
    icon: HandHeart,
    description: 'Day-to-day living support — bathing, mobility, meals, and warm companionship.'
  },
  {
    value: 'NURSE',
    label: 'Nurse',
    icon: ShieldCheck,
    description: 'Qualified clinical care — medication, wound dressing, vitals, and health monitoring.'
  }
];

const SERVICE_MODELS = [
  {
    value: 'SHIFT_BASED',
    label: 'Shift Based',
    icon: Clock,
    description: 'An attendant covers set daily shifts at the hospital — day or night.'
  },
  {
    value: 'LIVE_IN',
    label: 'Live In',
    icon: Home,
    description: 'An attendant stays with the patient full-time for round-the-clock care.'
  }
];

// Must match the options in ClientPatients.jsx (RELATIONSHIP_OPTIONS) so that a
// registered patient's stored relationship_to_client value matches an option here.
const RELATIONSHIP_OPTIONS = [
  'Self', 'Parent', 'Child', 'Sibling', 'Spouse / Partner',
  'Guardian', 'Caregiver', 'Friend', 'Neighbor', 'Other'
];

const HospitalCareBookingPage = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [staffData, setStaffData] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState('ALL');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [stepError, setStepError] = useState('');
  const [patients, setPatients] = useState([]);
  const [patientsLoading, setPatientsLoading] = useState(false);
  const [staffSearch, setStaffSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [staffPage, setStaffPage] = useState(1);
  const [staffTotalPages, setStaffTotalPages] = useState(1);
  const [staffTotalCount, setStaffTotalCount] = useState(0);
  const staffRequestIdRef = useRef(0);
  const [selectedPatientId, setSelectedPatientId] = useState(null);
  const [ownClientProfile, setOwnClientProfile] = useState(null);
  const isPatientLocked = !!selectedPatientId;

  const [formData, setFormData] = useState({
    // Payer Information
    payer_name: '',
    payer_mobile: '',
    payer_email: '',

    // Patient Information
    patient_name: '',
    patient_age: '',
    patient_gender: '',
    relationship: '',
    patient_condition: '',

    // Service Details
    service_type: 'HOSPITAL_CARE',
    service_model: '',
    hospital_name: '',
    home_address: '',
    latitude: '',
    longitude: '',
    start_date: '',
    end_date: '',
    remarks: '',
    preferred_gender: 'ANY',
    preferred_staff_id: null
  });

  const { user, isAuthenticated } = useAuth();

  // Prefill payer details when user is logged in (but don't overwrite existing input)
  useEffect(() => {
    if (!user) return;

    setFormData(prev => {
      // name can be in multiple places depending on user type
      const payerName = prev.payer_name
        || user.client_info?.name
        || user.staff_info?.name
        || user.full_name
        || user.name
        || (user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : '')
        || user.displayName
        || '';

      // mobile/phone fields
      const payerMobile = prev.payer_mobile
        || user.client_info?.mobile_number
        || user.staff_info?.mobile_number
        || user.mobile_number
        || user.mobile
        || user.phone
        || user.contact_number
        || '';

      // email fields - try several common locations
      const payerEmail = prev.payer_email
        || user.client_info?.email
        || user.client_info?.contact?.email
        || user.staff_info?.email
        || user.staff_info?.contact?.email
        || user.data?.email
        || user.user?.email
        || user.email
        || user.email_address
        || user.username
        || '';

      // Log user payload when email couldn't be resolved to help debugging
      if (!payerEmail) {
        // avoid noisy logs in production but useful during development
        // eslint-disable-next-line no-console
        console.debug('Prefill: could not find email in user payload', user);
      }

      if (prev.payer_name === payerName && prev.payer_mobile === payerMobile && prev.payer_email === payerEmail) {
        return prev;
      }

      return { ...prev, payer_name: payerName, payer_mobile: payerMobile, payer_email: payerEmail };
    });
  }, [user]);

  // Fetch patients for authenticated client
  useEffect(() => {
    const fetchPatients = async () => {
      if (!isAuthenticated || !user?.id) return;
      
      try {
        setPatientsLoading(true);
        console.log('Fetching patients for user:', user);
        
        // First get the client profile to get client_profile_id
        const clientProfileResponse = await apiClient.getClientProfileByUserId(user.id);
        console.log('Client profile response:', clientProfileResponse);
        
        if (clientProfileResponse.data?.client_profile_id) {
          setOwnClientProfile(clientProfileResponse.data);
          const response = await apiClient.getPatientsByClient(clientProfileResponse.data.client_profile_id);
          console.log('Patients response:', response);
          setPatients(response.data || []);
        } else {
          console.log('No client profile found for user');
          setOwnClientProfile(null);
          setPatients([]);
        }
      } catch (error) {
        console.error('Error fetching patients:', error);
        setOwnClientProfile(null);
        setPatients([]);
      } finally {
        setPatientsLoading(false);
      }
    };

    fetchPatients();
  }, [isAuthenticated, user?.id]);

  // When booking for "Self" as a registered client, lock the care profile
  // fields to the client's own registered details.
  const isSelfLocked = isAuthenticated && !!ownClientProfile && !selectedPatientId && formData.relationship === 'Self';

  useEffect(() => {
    if (!isSelfLocked) return;
    setFormData(prev => ({
      ...prev,
      patient_name: ownClientProfile.full_name || prev.patient_name,
      patient_gender: ownClientProfile.gender || prev.patient_gender,
      home_address: ownClientProfile.primary_address || prev.home_address
    }));
  }, [isSelfLocked, ownClientProfile]);

  // Clear the auto-filled details once the client moves off "Self" so a
  // different relationship never inherits the client's own name/gender.
  // Home address is left as-is since it's editable and not identity-locked.
  const wasSelfLockedRef = useRef(isSelfLocked);
  useEffect(() => {
    if (wasSelfLockedRef.current && !isSelfLocked && !selectedPatientId) {
      setFormData(prev => ({ ...prev, patient_name: '', patient_gender: '' }));
    }
    wasSelfLockedRef.current = isSelfLocked;
  }, [isSelfLocked, selectedPatientId]);

  // Handle patient selection from dropdown
  const handlePatientSelection = (patientId) => {
    if (!patientId) {
      // Clear patient fields
      setFormData(prev => ({
        ...prev,
        patient_name: '',
        patient_age: '',
        patient_gender: '',
        relationship: '',
        patient_condition: ''
      }));
      setSelectedPatientId(null);
      return;
    }

    const selectedPatient = patients.find(p => p.patient_id === patientId);
    if (selectedPatient) {
      setFormData(prev => ({
        ...prev,
        patient_name: selectedPatient.full_name,
        patient_age: selectedPatient.age.toString(),
        patient_gender: selectedPatient.gender || '',
        relationship: selectedPatient.relationship_to_client || 'Other',
        patient_condition: selectedPatient.medical_condition || ''
      }));
      setSelectedPatientId(patientId);
    }
  };

  const totalSteps = 4;

  // Search runs on the server (any caregiver can be found, not just the first page).
  // Wait for a pause in typing, then jump back to page 1 with the new term.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(staffSearch.trim());
      setStaffPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [staffSearch]);

  useEffect(() => {
    if (currentStep !== 4) return;
    fetchStaffData();
  }, [currentStep, activeFilter, debouncedSearch, staffPage, formData.preferred_gender]);

  const fetchStaffData = async () => {
    // Only the latest request may update the screen (typing fast fires several).
    const requestId = ++staffRequestIdRef.current;
    try {
      setLoading(true);
      setError(null);

      const roleParam = activeFilter === 'ALL' ? 'NURSE,CARETAKER' : activeFilter;
      const params = {
        status: 'AVAILABLE',
        limit: STAFF_PAGE_SIZE,
        page: staffPage,
        public_only: true,
        ...(debouncedSearch && { search: debouncedSearch })
      };
      const genderPreference = formData.preferred_gender;

      const response = genderPreference && genderPreference !== 'ANY'
        ? await apiClient.getStaffByGender(genderPreference, { ...params, role: roleParam })
        : await apiClient.getStaffByRole(roleParam, params);
      if (requestId !== staffRequestIdRef.current) return;

      setStaffData((response.data || []).map(staff => {
        const isNurse = staff.role.includes('NURSE');
        return toCaregiverCard(staff, isNurse ? 'Nurse' : 'Caretaker', isNurse ? 'NURSE' : 'CARETAKER');
      }));
      setStaffTotalPages(response.pagination?.total_pages || 1);
      setStaffTotalCount(response.pagination?.total_count || 0);
    } catch (err) {
      if (requestId !== staffRequestIdRef.current) return;
      console.error('Error fetching staff data:', err);
      setError('Failed to load staff data. Please try again later.');
    } finally {
      if (requestId === staffRequestIdRef.current) setLoading(false);
    }
  };

  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
    setStaffPage(1);
  };

  const handleStaffSelect = (staff) => {
    setSelectedStaff(staff);
    setFormData({ ...formData, preferred_staff_id: staff.id });
  };

  // Returns an error message when the given step is incomplete, otherwise ''
  const validateStep = (step) => {
    const filled = (v) => String(v ?? '').trim().length > 0;
    if (step === 1) {
      if (!filled(formData.payer_name)) return 'Please enter the payer name.';
      if (!filled(formData.payer_mobile)) return 'Please enter a mobile number.';
      if (filled(formData.payer_email) && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.payer_email.trim())) {
        return 'Please enter a valid email address.';
      }
    }
    if (step === 2) {
      if (!filled(formData.patient_name)) return 'Please enter the patient name or select a care profile.';
      if (!filled(formData.patient_age) || Number(formData.patient_age) <= 0) return 'Please enter a valid patient age.';
      if (!filled(formData.patient_gender)) return 'Please select the patient gender.';
      if (!filled(formData.relationship)) return 'Please select the relationship to the client.';
    }
    if (step === 3) {
      if (!filled(formData.hospital_name)) return 'Please enter the hospital the patient is admitted at.';
      if (!filled(formData.home_address)) return 'Please enter the service address.';
      if (!filled(formData.start_date)) return 'Please select a start date.';
      if (formData.start_date < todayISO()) return 'The start date cannot be in the past.';
      if (filled(formData.end_date) && formData.end_date <= formData.start_date) return 'The end date must be after the start date.';
      if (!SERVICE_TYPES.some(opt => opt.value === formData.service_type)) return 'Please choose the type of caregiver.';
      if (!SERVICE_MODELS.some(opt => opt.value === formData.service_model)) return 'Please choose a service model.';
    }
    return '';
  };

  const nextStep = () => {
    console.log('nextStep called, currentStep:', currentStep, 'totalSteps:', totalSteps);
    const stepProblem = validateStep(currentStep);
    if (stepProblem) {
      setStepError(stepProblem);
      return;
    }
    setStepError('');
    if (currentStep < totalSteps) {
      const newStep = currentStep + 1;
      console.log('Advancing to step:', newStep);
      setCurrentStep(newStep);
    } else {
      console.log('Cannot advance - already at final step');
    }
  };

  const prevStep = () => {
    console.log('prevStep called, currentStep:', currentStep);
    setStepError('');
    if (currentStep > 1) setCurrentStep(prev => prev - 1);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      console.log('Enter key pressed on step:', currentStep);
      // Only allow navigation on steps 1-3
      if (currentStep < 4) {
        console.log('Going to next step via Enter key');
        nextStep();
      }
      // On step 4, do nothing - user must click submit button
    }
  };

  // Only allow handleKeyDown on steps 1-3, not on step 4
  const shouldHandleKeyDown = () => {
    return currentStep < 4;
  };

  const handleSubmit = async (e) => {
    console.log('handleSubmit called on step:', currentStep);
    if (e) e.preventDefault();
    for (let step = 1; step <= 3; step++) {
      const stepProblem = validateStep(step);
      if (stepProblem) {
        setCurrentStep(step);
        setStepError(stepProblem);
        return;
      }
    }
    setIsSubmitting(true);
    setSubmitError('');

    try {
      // Prepare form data, ensuring empty strings are converted to null for numeric fields
      const submissionData = {
        ...formData,
        latitude: formData.latitude || null,
        longitude: formData.longitude || null,
      };

      const response = await apiClient.submitServiceRequest(submissionData);
      console.log('Booking request submitted successfully:', response);

      // Navigate to success page
      navigate('/booking-success', {
        state: {
          requestId: response.data?.request_id,
          requestCode: response.data?.service_request_code,
          selectedStaff: selectedStaff
        }
      });

    } catch (error) {
      console.error('Booking submission error:', error);
      setSubmitError(error.message || 'Failed to submit booking request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Animation variants
  const slideVariants = {
    hidden: { opacity: 0, x: 20 },
    visible: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -20 }
  };

  return (
    <div className="relative min-h-screen">
      {/* Full-page background image, pinned to the viewport so it stays proportional */}
      <div
        className="fixed inset-0 z-0"
        style={{
          backgroundImage: `url(${elderlyCareBg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      />
      <div className="fixed inset-0 z-0 bg-slate-900/40" />

      <Navbar />

      <div className="relative z-10 max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
            Book Hospital Care Service
          </h1>
          <p className="text-lg text-slate-100 max-w-3xl mx-auto">
            Arrange a trained attendant to stay with your loved one during a hospital admission
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex justify-start sm:justify-center mb-8 sm:mb-12 overflow-x-auto -mx-4 px-4">
          <div className="flex items-center gap-2 sm:gap-4">
            {[
              { id: 1, title: "Payer Details", icon: User },
              { id: 2, title: "Care Profile Info", icon: UserCheck },
              { id: 3, title: "Service Details", icon: Heart },
              { id: 4, title: "Choose Caregiver", icon: CheckCircle }
            ].map((step, index) => (
              <React.Fragment key={step.id}>
                <div className="flex flex-col items-center flex-shrink-0">
                  <div className={`p-2.5 sm:p-3 rounded-full ${currentStep >= step.id ? 'bg-sky-600 text-white border-sky-600' : 'bg-white text-slate-400 border-slate-300'}`}>
                    <step.icon className="w-5 h-5 sm:w-6 sm:h-6" />
                  </div>
                  <span className={`text-[11px] sm:text-sm font-medium mt-2 whitespace-nowrap ${currentStep >= step.id ? 'text-white' : 'text-slate-300'}`}>
                    {step.title}
                  </span>
                </div>
                {index < 3 && (
                  <div className={`h-0.5 w-8 sm:w-16 flex-shrink-0 transition-all ${currentStep > step.id ? 'bg-sky-600' : 'bg-white/30'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Form Content */}
        <div className="bg-white/90 backdrop-blur-md rounded-3xl sm:rounded-[32px] shadow-xl border border-white/40 overflow-hidden">
          <div className="p-5 sm:p-8 md:p-12">
            <form onSubmit={(e) => e.preventDefault()}>
              <AnimatePresence mode="wait">

                {/* Step 1: Payer Details */}
                {currentStep === 1 && (
                  <motion.div
                    key="step1"
                    variants={slideVariants}
                    initial="hidden" animate="visible" exit="exit"
                    className="space-y-6"
                  >
                    <h2 className="text-2xl font-bold text-slate-800 mb-6">Payer Information</h2>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="md:col-span-2">
                        <label className="text-sm font-semibold text-slate-600 block mb-1">Full Name</label>
                        <input
                          type="text"
                          className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-sky-500 outline-none text-slate-900 placeholder:text-slate-400"
                          value={formData.payer_name}
                          onChange={e => setFormData({ ...formData, payer_name: e.target.value })}
                          onKeyDown={shouldHandleKeyDown() ? handleKeyDown : undefined}
                          placeholder="e.g. John Doe"
                          required
                        />
                      </div>
                      <div>
                        <label className="text-sm font-semibold text-slate-600 block mb-1">Mobile Number</label>
                        <PhoneInput
                          name="payer_mobile"
                          value={formData.payer_mobile}
                          onChange={e => setFormData({ ...formData, payer_mobile: e.target.value })}
                          onKeyDown={shouldHandleKeyDown() ? handleKeyDown : undefined}
                          placeholder="e.g. 0771234567"
                          required
                        />
                      </div>
                      <div>
                        <label className="text-sm font-semibold text-slate-600 block mb-1">Email Address</label>
                        <input
                          type="email"
                          className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-sky-500 outline-none text-slate-900 placeholder:text-slate-400"
                          value={formData.payer_email}
                          onChange={e => setFormData({ ...formData, payer_email: e.target.value })}
                          onKeyDown={shouldHandleKeyDown() ? handleKeyDown : undefined}
                          placeholder="e.g. john@example.com"
                        />
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Step 2: Patient Information */}
                {currentStep === 2 && (
                  <motion.div
                    key="step2"
                    variants={slideVariants}
                    initial="hidden" animate="visible" exit="exit"
                    className="space-y-6"
                  >
                    <h2 className="text-2xl font-bold text-slate-800 mb-6">Care Profile Information</h2>
                    
                    {/* Patient Selection for Authenticated Clients */}
                    {isAuthenticated && patients.length > 0 && (
                      <div className="md:col-span-2 mb-6">
                        <label className="text-sm font-semibold text-slate-600 block mb-3">
                          Select Registered Care Profile
                        </label>
                        <div className="grid gap-3 mb-4">
                          {patients.map(patient => (
                            <div
                              key={patient.patient_id}
                              onClick={() => handlePatientSelection(selectedPatientId === patient.patient_id ? '' : patient.patient_id)}
                              className={`p-4 border-2 rounded-xl cursor-pointer transition-all duration-200 hover:shadow-md ${
                                selectedPatientId === patient.patient_id
                                  ? 'border-sky-500 bg-sky-50'
                                  : 'border-slate-200 bg-white hover:border-sky-300'
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <h4 className="font-semibold text-slate-800 text-lg">{patient.full_name}</h4>
                                  <div className="flex gap-4 mt-1 text-sm text-slate-600">
                                    <span className="flex items-center gap-1">
                                      <User className="w-4 h-4" />
                                      Age: {patient.age}
                                    </span>
                                    {patient.gender && (
                                      <span className="flex items-center gap-1">
                                        <UserCheck className="w-4 h-4" />
                                        {patient.gender.charAt(0) + patient.gender.slice(1).toLowerCase()}
                                      </span>
                                    )}
                                    <span className="flex items-center gap-1">
                                      <Heart className="w-4 h-4" />
                                      {patient.relationship_to_client || 'Other'}
                                    </span>
                                  </div>
                                  {patient.medical_condition && (
                                    <p className="text-xs text-slate-500 mt-2">
                                      <strong>Medical Condition:</strong> {patient.medical_condition}
                                    </p>
                                  )}
                                </div>
                                <div className="ml-4">
                                  {selectedPatientId === patient.patient_id && (
                                    <div className="w-6 h-6 bg-sky-500 rounded-full flex items-center justify-center">
                                      <CheckCircle className="w-4 h-4 text-white" />
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          <button
                            onClick={() => handlePatientSelection('')}
                            className="text-sky-600 hover:text-sky-700 underline"
                          >
                            Clear selection
                          </button>
                          <span>•</span>
                          <span>Or clear selection to fill in a new care profile</span>
                        </div>
                      </div>
                    )}

                    {isPatientLocked && (
                      <div className="md:col-span-2 mb-4 flex items-start gap-2 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-xl p-3">
                        <ShieldCheck className="w-4 h-4 text-sky-600 flex-shrink-0 mt-0.5" />
                        <span>Details for this registered care profile are locked. Clear the selection above if you need to change them.</span>
                      </div>
                    )}

                    {isSelfLocked && (
                      <div className="md:col-span-2 mb-4 flex items-start gap-2 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-xl p-3">
                        <ShieldCheck className="w-4 h-4 text-sky-600 flex-shrink-0 mt-0.5" />
                        <span>You're booking for yourself — your registered name and gender are locked in automatically, and your home address is pre-filled but editable. Change "Relationship to Client" if this is for someone else.</span>
                      </div>
                    )}

                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="md:col-span-2">
                        <label className="text-sm font-semibold text-slate-600 block mb-1">
                          Full Name
                          {isAuthenticated && patients.length > 0 && (
                            <span className="ml-2 text-xs text-sky-600">(Required if no care profile selected above)</span>
                          )}
                        </label>
                        <input
                          type="text"
                          className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-sky-500 outline-none text-slate-900 placeholder:text-slate-400 disabled:opacity-70 disabled:cursor-not-allowed"
                          value={formData.patient_name}
                          onChange={e => setFormData({ ...formData, patient_name: e.target.value })}
                          onKeyDown={shouldHandleKeyDown() ? handleKeyDown : undefined}
                          placeholder="e.g. Jane Doe"
                          required={!isAuthenticated || patients.length === 0}
                          disabled={isPatientLocked || isSelfLocked}
                        />
                      </div>
                      <div>
                        <label className="text-sm font-semibold text-slate-600 block mb-1">Age</label>
                        <input
                          type="number"
                          className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-sky-500 outline-none text-slate-900 placeholder:text-slate-400 disabled:opacity-70 disabled:cursor-not-allowed"
                          value={formData.patient_age}
                          onChange={e => setFormData({ ...formData, patient_age: e.target.value })}
                          onKeyDown={shouldHandleKeyDown() ? handleKeyDown : undefined}
                          placeholder="e.g. 75"
                          required
                          disabled={isPatientLocked}
                        />
                      </div>
                      <div>
                        <label className="text-sm font-semibold text-slate-600 block mb-1">Gender</label>
                        <select
                          className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-sky-500 outline-none text-slate-900 disabled:opacity-70 disabled:cursor-not-allowed"
                          value={formData.patient_gender}
                          onChange={e => setFormData({ ...formData, patient_gender: e.target.value })}
                          onKeyDown={shouldHandleKeyDown() ? handleKeyDown : undefined}
                          required
                          disabled={(isPatientLocked || isSelfLocked) && !!formData.patient_gender}
                        >
                          <option value="">Select gender</option>
                          <option value="MALE">Male</option>
                          <option value="FEMALE">Female</option>
                          <option value="OTHER">Other</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-sm font-semibold text-slate-600 block mb-1">Relationship to Client</label>
                        <select
                          className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-sky-500 outline-none text-slate-900 disabled:opacity-70 disabled:cursor-not-allowed"
                          value={formData.relationship}
                          onChange={e => setFormData({ ...formData, relationship: e.target.value })}
                          onKeyDown={shouldHandleKeyDown() ? handleKeyDown : undefined}
                          required
                          disabled={isPatientLocked}
                        >
                          <option value="">Select relationship</option>
                          {RELATIONSHIP_OPTIONS.map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      </div>
                      <div className="md:col-span-2">
                        <label className="text-sm font-semibold text-slate-600 block mb-1">Medical Condition (Optional)</label>
                        <textarea
                          rows="3"
                          className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-sky-500 outline-none resize-none text-slate-900 placeholder:text-slate-400 disabled:opacity-70 disabled:cursor-not-allowed"
                          value={formData.patient_condition}
                          onChange={e => setFormData({ ...formData, patient_condition: e.target.value })}
                          onKeyDown={shouldHandleKeyDown() ? handleKeyDown : undefined}
                          placeholder="Any medical conditions or special requirements..."
                          disabled={isPatientLocked}
                        />
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Step 3: Service Details */}
                {currentStep === 3 && (
                  <motion.div
                    key="step3"
                    variants={slideVariants}
                    initial="hidden" animate="visible" exit="exit"
                    className="space-y-6"
                  >
                    <h2 className="text-2xl font-bold text-slate-800 mb-6">Service Details</h2>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="md:col-span-2">
                        <label className="text-sm font-semibold text-slate-600 block mb-1">Hospital (where the patient is admitted)</label>
                        <input
                          type="text"
                          className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-sky-500 outline-none text-slate-900 placeholder:text-slate-400"
                          value={formData.hospital_name}
                          onChange={e => setFormData({ ...formData, hospital_name: e.target.value })}
                          onKeyDown={shouldHandleKeyDown() ? handleKeyDown : undefined}
                          placeholder="e.g. Asiri Central Hospital, Colombo"
                          maxLength={255}
                          required
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="text-sm font-semibold text-slate-600 block mb-1">Home Address</label>
                        <textarea
                          rows="3"
                          className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-sky-500 outline-none resize-none text-slate-900 placeholder:text-slate-400"
                          value={formData.home_address}
                          onChange={e => setFormData({ ...formData, home_address: e.target.value })}
                          onKeyDown={shouldHandleKeyDown() ? handleKeyDown : undefined}
                          placeholder="Full address for care service"
                          required
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="text-sm font-semibold text-slate-600 block mb-1">Service Dates <span className="font-normal text-slate-400">(end date is optional)</span></label>
                        <DateRangePicker
                          theme="sky"
                          startDate={formData.start_date}
                          endDate={formData.end_date}
                          onChange={({ startDate, endDate }) => setFormData({ ...formData, start_date: startDate, end_date: endDate })}
                        />
                      </div>
                      <div>
                        <label className="text-sm font-semibold text-slate-600 block mb-1">Preferred Caregiver Gender</label>
                        <select
                          className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-sky-500 outline-none text-slate-900"
                          value={formData.preferred_gender}
                          onChange={e => {
                            setFormData({ ...formData, preferred_gender: e.target.value });
                            // A new preference means a new list, starting from page 1
                            setStaffPage(1);
                          }}
                          onKeyDown={shouldHandleKeyDown() ? handleKeyDown : undefined}
                        >
                          <option value="ANY">No Preference</option>
                          <option value="MALE">Male</option>
                          <option value="FEMALE">Female</option>
                        </select>
                      </div>

                      {/* Service Type — card selection */}
                      <div className="md:col-span-2">
                        <label className="text-sm font-semibold text-slate-600 block mb-3">Service Type</label>
                        <div className="grid sm:grid-cols-2 gap-4">
                          {SERVICE_TYPES.map((opt) => {
                            const isSelected = formData.service_type === opt.value;
                            return (
                              <button
                                type="button"
                                key={opt.value}
                                onClick={() => setFormData({ ...formData, service_type: opt.value })}
                                className={`text-left p-4 rounded-xl border-2 transition-all ${isSelected
                                    ? 'border-sky-500 bg-sky-50'
                                    : 'border-slate-200 bg-white hover:border-sky-300'
                                  }`}
                              >
                                <div className="flex items-start gap-3">
                                  <div className={`p-2 rounded-lg ${isSelected ? 'bg-sky-600 text-white' : 'bg-slate-100 text-slate-500'}`}>
                                    <opt.icon className="w-5 h-5" />
                                  </div>
                                  <div className="flex-1">
                                    <div className="flex items-center justify-between">
                                      <h4 className="font-semibold text-slate-800">{opt.label}</h4>
                                      {isSelected && <CheckCircle className="w-5 h-5 text-sky-600 flex-shrink-0" />}
                                    </div>
                                    <p className="text-xs text-slate-500 mt-1">{opt.description}</p>
                                  </div>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Service Model — card selection */}
                      <div className="md:col-span-2">
                        <label className="text-sm font-semibold text-slate-600 block mb-3">Service Model</label>
                        <div className="grid sm:grid-cols-3 gap-4">
                          {SERVICE_MODELS.map((opt) => {
                            const isSelected = formData.service_model === opt.value;
                            return (
                              <button
                                type="button"
                                key={opt.value}
                                onClick={() => setFormData({ ...formData, service_model: opt.value })}
                                className={`text-left p-4 rounded-xl border-2 transition-all ${isSelected
                                    ? 'border-sky-500 bg-sky-50'
                                    : 'border-slate-200 bg-white hover:border-sky-300'
                                  }`}
                              >
                                <div className="flex items-center justify-between mb-2">
                                  <div className={`p-2 rounded-lg ${isSelected ? 'bg-sky-600 text-white' : 'bg-slate-100 text-slate-500'}`}>
                                    <opt.icon className="w-5 h-5" />
                                  </div>
                                  {isSelected && <CheckCircle className="w-5 h-5 text-sky-600 flex-shrink-0" />}
                                </div>
                                <h4 className="font-semibold text-slate-800">{opt.label}</h4>
                                <p className="text-xs text-slate-500 mt-1">{opt.description}</p>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      <div className="md:col-span-2">
                        <label className="text-sm font-semibold text-slate-600 block mb-1">Additional Remarks (Optional)</label>
                        <textarea
                          rows="3"
                          className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-sky-500 outline-none resize-none text-slate-900 placeholder:text-slate-400"
                          value={formData.remarks}
                          onChange={e => setFormData({ ...formData, remarks: e.target.value })}
                          onKeyDown={shouldHandleKeyDown() ? handleKeyDown : undefined}
                          placeholder="Any special requirements or notes..."
                        />
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Step 4: Choose Caregiver */}
                {currentStep === 4 && (
                  <motion.div
                    key="step4"
                    variants={slideVariants}
                    initial="hidden" animate="visible" exit="exit"
                    className="space-y-6"
                  >
                    <h2 className="text-2xl font-bold text-slate-800 mb-6">Choose Your Caregiver</h2>

                    {/* No Specific Staff Option */}
                    <div
                      onClick={() => {
                        setSelectedStaff(null);
                        setFormData({ ...formData, preferred_staff_id: null });
                      }}
                      className={`relative p-6 rounded-2xl border-2 cursor-pointer transition-all mb-6 ${!selectedStaff
                          ? 'border-sky-500 bg-sky-50'
                          : 'border-slate-200 hover:border-slate-300 bg-white'
                        }`}
                    >
                      {!selectedStaff && (
                        <div className="absolute top-4 right-4 w-6 h-6 bg-sky-600 rounded-full flex items-center justify-center">
                          <CheckCircle className="w-4 h-4 text-white" />
                        </div>
                      )}

                      <div className="flex items-center gap-4">
                        <div className="w-16 h-16 rounded-full bg-slate-200 flex items-center justify-center">
                          <User className="w-8 h-8 text-slate-500" />
                        </div>
                        <div>
                          <h3 className="font-bold text-slate-900">No Specific Caregiver</h3>
                          <p className="text-sm text-slate-600">Let us assign the best available caregiver based on your requirements</p>
                        </div>
                      </div>
                    </div>

                    {/* Search & Filter */}
                    <div className="flex flex-col sm:flex-row gap-3 mb-6">
                      <div className="relative flex-1">
                        <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <input
                          type="text"
                          value={staffSearch}
                          onChange={e => setStaffSearch(e.target.value)}
                          placeholder="Search any caregiver by name or location..."
                          className="w-full pl-9 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-900 placeholder:text-slate-400 focus:ring-2 focus:ring-sky-500 outline-none"
                        />
                      </div>
                      <div className="flex items-center gap-2 bg-slate-100 rounded-full p-1">
                        <button
                          onClick={() => handleFilterChange('ALL')}
                          className={`px-5 py-2 rounded-full font-medium transition-all text-sm ${activeFilter === 'ALL' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
                        >
                          All Staff
                        </button>
                        <button
                          onClick={() => handleFilterChange('NURSE')}
                          className={`px-5 py-2 rounded-full font-medium transition-all text-sm ${activeFilter === 'NURSE' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
                        >
                          Nurses
                        </button>
                        <button
                          onClick={() => handleFilterChange('CARETAKER')}
                          className={`px-5 py-2 rounded-full font-medium transition-all text-sm ${activeFilter === 'CARETAKER' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
                        >
                          Caretakers
                        </button>
                      </div>
                    </div>

                    {/* Loading State */}
                    {loading && (
                      <div className="flex flex-col items-center justify-center py-16">
                        <Loader2 className="w-8 h-8 text-sky-600 animate-spin mb-4" />
                        <p className="text-slate-600">Loading compassionate caregivers...</p>
                      </div>
                    )}

                    {/* Error State */}
                    {error && (
                      <div className="text-center py-16">
                        <p className="text-red-600 mb-4">{error}</p>
                        <button
                          onClick={fetchStaffData}
                          className="px-6 py-2 bg-sky-600 text-white rounded-full font-medium hover:bg-sky-700 transition-colors"
                        >
                          Try Again
                        </button>
                      </div>
                    )}

                    {/* Staff Selection */}
                    {!loading && !error && (
                      <>
                        <p className="text-sm text-slate-500 mb-4">
                          {staffTotalCount === 0
                            ? 'No caregivers found'
                            : `${staffTotalCount} caregiver${staffTotalCount === 1 ? '' : 's'} available`}
                          {debouncedSearch && <> for &ldquo;{debouncedSearch}&rdquo;</>}
                        </p>

                        {staffData.length === 0 ? (
                          <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 py-14 text-center">
                            <p className="font-semibold text-slate-700">No caregivers match your search</p>
                            <p className="text-sm text-slate-500 mt-1">Try a different name, or clear the search and filters.</p>
                          </div>
                        ) : (
                          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {staffData.map((staff) => (
                              <div
                                key={staff.id}
                                onClick={() => handleStaffSelect(staff)}
                                className={`relative p-6 rounded-2xl border-2 cursor-pointer transition-all ${selectedStaff?.id === staff.id
                                    ? 'border-sky-500 bg-sky-50'
                                    : 'border-slate-200 hover:border-slate-300 bg-white'
                                  }`}
                              >
                                {selectedStaff?.id === staff.id && (
                                  <div className="absolute top-4 right-4 w-6 h-6 bg-sky-600 rounded-full flex items-center justify-center">
                                    <CheckCircle className="w-4 h-4 text-white" />
                                  </div>
                                )}

                                <div className="flex items-center gap-4 mb-4">
                                  {staff.image ? (
                                    <img
                                      src={staff.image}
                                      alt={staff.name}
                                      className="w-16 h-16 rounded-full object-cover flex-shrink-0"
                                    />
                                  ) : (
                                    <div className="w-16 h-16 rounded-full bg-slate-100 flex-shrink-0" />
                                  )}
                                  <div className="min-w-0 pr-6">
                                    <h3 className="font-bold text-slate-900 truncate">{staff.name}</h3>
                                    <p className="text-sm text-slate-600">{staff.role}</p>
                                  </div>
                                </div>

                                <div className="space-y-2">
                                  <div className="flex items-center gap-2 text-sm text-slate-600">
                                    <Star className={`w-4 h-4 fill-current ${staff.rating ? 'text-yellow-400' : 'text-slate-300'}`} />
                                    {staff.rating
                                      ? <span>{staff.rating} <span className="text-slate-400">({staff.reviews} {staff.reviews === 1 ? 'review' : 'reviews'})</span></span>
                                      : <span className="text-slate-400">No reviews yet</span>
                                    }
                                  </div>
                                  {staff.location && (
                                    <div className="flex items-center gap-2 text-sm text-slate-600">
                                      <MapPin className="w-4 h-4 flex-shrink-0" />
                                      <span className="truncate">{staff.location}</span>
                                    </div>
                                  )}
                                  {staff.experience && (
                                    <div className="flex items-center gap-2 text-sm text-slate-600">
                                      <Clock className="w-4 h-4 flex-shrink-0" />
                                      <span>{staff.experience} experience</span>
                                    </div>
                                  )}
                                  {staff.languages.length > 0 && (
                                    <div className="flex items-center gap-2 text-sm text-slate-600">
                                      <User className="w-4 h-4 flex-shrink-0" />
                                      <span className="truncate">
                                        {staff.languages.slice(0, 3).join(', ')}
                                        {staff.languages.length > 3 && ` +${staff.languages.length - 3}`}
                                      </span>
                                    </div>
                                  )}
                                </div>

                                {staff.badges.length > 0 && (
                                  <div className="flex flex-wrap gap-1.5 mt-3">
                                    {staff.badges.map((badge, i) => (
                                      <span key={i} className="px-2 py-0.5 rounded-md bg-sky-100 text-sky-700 text-xs font-semibold truncate max-w-full">
                                        {badge}
                                      </span>
                                    ))}
                                  </div>
                                )}

                                {staff.isVerified && (
                                  <div className="flex items-center gap-1 mt-3">
                                    <ShieldCheck className="w-4 h-4 text-green-600" />
                                    <span className="text-xs text-green-600 font-medium">Verified</span>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}

                        {staffTotalPages > 1 && (
                          <div className="flex items-center justify-center gap-4 mt-8">
                            <button
                              type="button"
                              onClick={() => setStaffPage(p => Math.max(1, p - 1))}
                              disabled={staffPage === 1}
                              className="inline-flex items-center gap-1 px-4 py-2 rounded-xl border border-slate-200 bg-white text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              <ChevronLeft className="w-4 h-4" /> Previous
                            </button>
                            <span className="text-sm text-slate-500">Page {staffPage} of {staffTotalPages}</span>
                            <button
                              type="button"
                              onClick={() => setStaffPage(p => Math.min(staffTotalPages, p + 1))}
                              disabled={staffPage >= staffTotalPages}
                              className="inline-flex items-center gap-1 px-4 py-2 rounded-xl border border-slate-200 bg-white text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              Next <ChevronRight className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </motion.div>
                )}

              </AnimatePresence>

              {stepError && (
                <div role="alert" className="mt-6 px-4 py-3 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm font-medium">
                  {stepError}
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex justify-between mt-8">
                <button
                  type="button"
                  onClick={prevStep}
                  disabled={currentStep === 1}
                  className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all ${currentStep === 1
                      ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                      : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                    }`}
                >
                  <ChevronLeft className="w-5 h-5" />
                  Previous
                </button>

                {currentStep < totalSteps ? (
                  <button
                    type="button"
                    onClick={nextStep}
                    className="flex items-center gap-2 px-6 py-3 bg-sky-600 text-white rounded-xl font-medium hover:bg-sky-700 transition-colors"
                  >
                    Next
                    <ChevronRight className="w-5 h-5" />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className="flex items-center gap-2 px-6 py-3 bg-sky-600 text-white rounded-xl font-medium hover:bg-sky-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-5 h-5" />
                        Confirm Booking
                      </>
                    )}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>

      <div className="relative z-10">
        <Footer />
      </div>
    </div>
  );
};

export default HospitalCareBookingPage;
